import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, MessageSquare } from 'lucide-react';
import { api, API_BASE_URL, Chapter } from '../services/api';
import { ChatMessage } from '../types';
import ContentViewer from '../components/ContentViewer';
import ChatPanel from '../components/ChatPanel';

type LessonLanguage = 'en' | 'zh';

const prefixElementIds = (root: HTMLElement, prefix: string) => {
  const idMap = new Map<string, string>();
  root.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
    const oldId = element.id;
    const newId = oldId.startsWith(`${prefix}-`) ? oldId : `${prefix}-${oldId}`;
    element.id = newId;
    idMap.set(oldId, newId);
  });

  root.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
    const oldId = anchor.getAttribute('href')?.slice(1);
    if (oldId && idMap.has(oldId)) {
      anchor.setAttribute('href', `#${idMap.get(oldId)}`);
    }
  });
};

const getLanguageContent = (content: string, language: LessonLanguage, scopePrefix: string): string => {
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const panels = Array.from(doc.querySelectorAll<HTMLElement>('[data-language]'));
  panels.forEach((panel) => {
    if (panel.dataset.language !== language) {
      panel.remove();
      return;
    }

    const fragment = doc.createDocumentFragment();
    while (panel.firstChild) fragment.appendChild(panel.firstChild);
    panel.replaceWith(fragment);
  });
  prefixElementIds(doc.body, scopePrefix);
  return doc.body.innerHTML;
};

const getChapterTitle = (chapter: Chapter, language: LessonLanguage): string => {
  if (!chapter.content) return chapter.title;
  const html = getLanguageContent(chapter.content, language, `chapter-${chapter.chapter_number}`);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('h1')?.textContent?.trim() || chapter.title;
};

const extractPracticeContent = (chapter: Chapter, language: LessonLanguage): string => {
  if (!chapter.content) return '';
  const html = getLanguageContent(chapter.content, language, `chapter-${chapter.chapter_number}`);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('.crumbs, .eyebrow').forEach((element) => element.remove());
  doc.querySelectorAll<HTMLElement>('.answer-card .nb-code.ds-code-runner').forEach((codeBlock) => {
    codeBlock.classList.remove('ds-code-runner');
    codeBlock.removeAttribute('data-language');
    codeBlock.removeAttribute('data-enhanced');
    codeBlock.querySelectorAll('.ds-code-editor-shell, .ds-original-code').forEach((element) => element.remove());
  });
  const practice = doc.querySelector<HTMLElement>('.ds-practice-section');
  return practice?.innerHTML || '';
};

export default function Practice() {
  const { courseId, chapterNumber } = useParams<{ courseId: string; chapterNumber: string }>();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const defaultWelcomeMessage: ChatMessage = {
    role: 'assistant',
    content: 'Hi! I can help with these practice questions. Select text or ask about your code, data, or reasoning.'
  };
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([defaultWelcomeMessage]);
  const [showChat, setShowChat] = useState(true);
  const [language, setLanguage] = useState<LessonLanguage>(() => {
    return localStorage.getItem('ds101_lesson_language') === 'zh' ? 'zh' : 'en';
  });

  const currentNumber = Number(chapterNumber || 1);
  const chapter = chapters.find(item => item.chapter_number === currentNumber) || null;

  useEffect(() => {
    if (!courseId) return;
    api.getPublishedChapters(Number(courseId))
      .then(setChapters)
      .catch(console.error)
      .finally(() => setIsLoading(false));
    loadChatHistory(Number(courseId));
  }, [courseId]);

  useEffect(() => {
    if (courseId && chatMessages.length > 1) {
      const timeoutId = setTimeout(() => {
        api.saveChatHistory(Number(courseId), chatMessages).catch(console.error);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [chatMessages, courseId]);

  useEffect(() => {
    localStorage.setItem('ds101_lesson_language', language);
  }, [language]);

  const practiceContent = useMemo(() => {
    return chapter ? extractPracticeContent(chapter, language) : '';
  }, [chapter, language]);

  const goToChapter = (nextNumber: number) => {
    if (!courseId || nextNumber < 1 || nextNumber > chapters.length) return;
    navigate(`/practice/${courseId}/${nextNumber}`);
  };

  const loadChatHistory = async (id: number) => {
    try {
      const history = await api.getChatHistory(id);
      if (history && history.length > 0) setChatMessages(history);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const clearChatHistory = async () => {
    if (!courseId) return;
    try {
      await api.clearChatHistory(Number(courseId));
      setChatMessages([defaultWelcomeMessage]);
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  };

  const handleAskQuestion = (selectedText: string, question: string) => {
    const userMessage: ChatMessage = {
      role: 'user',
      content: `user selected "${selectedText.substring(0, 100)}${selectedText.length > 100 ? '...' : ''}" in the practice notebook and ask: ${question}`,
    };

    setChatMessages(prev => [...prev, userMessage]);
    setShowChat(true);

    fetch(`${API_BASE_URL}/student/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({
        messages: [...chatMessages, userMessage],
        context: selectedText,
        stream: true,
      }),
    }).then(async (response) => {
      if (!response.ok) throw new Error('Failed to get response');
      const reader = response.body?.getReader();
      if (!reader) return;

      let content = '';
      setChatMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        content += new TextDecoder().decode(value);
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content },
        ]);
      }
    }).catch((error) => {
      console.error('Chat error:', error);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    });
  };

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-apple-blue animate-spin" />
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-apple-text mb-4">Practice not found</h1>
          <button onClick={() => navigate(`/learn/${courseId}`)} className="btn-primary">
            Back to lesson
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex bg-white">
      <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-apple-border shadow-apple-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <button
              onClick={() => navigate(`/learn/${courseId}`)}
              className="inline-flex items-center gap-2 text-sm text-apple-text-secondary hover:text-apple-text mb-1"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'en' ? 'Back to lesson' : '返回课程'}
            </button>
            <h1 className="text-xl font-semibold text-apple-text truncate">
              {getChapterTitle(chapter, language)}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-apple-border bg-white p-1">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  language === 'en' ? 'bg-apple-blue text-white' : 'text-apple-text-secondary hover:text-apple-text'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('zh')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  language === 'zh' ? 'bg-apple-blue text-white' : 'text-apple-text-secondary hover:text-apple-text'
                }`}
              >
                中文
              </button>
            </div>
            <button
              onClick={() => setShowChat(!showChat)}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              {showChat ? 'Hide Chat' : 'Show Chat'}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {practiceContent ? (
          <ContentViewer content={practiceContent} onAskQuestion={handleAskQuestion} />
        ) : (
          <div className="text-center py-16 text-apple-text-secondary">
            {language === 'en' ? 'No practice content for this week.' : '本周暂无习题内容。'}
          </div>
        )}

        <div className="flex items-center justify-between mt-12 pt-8 border-t border-apple-border">
          <button
            onClick={() => goToChapter(currentNumber - 1)}
            disabled={currentNumber <= 1}
            className="btn-outline flex items-center gap-2 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            {language === 'en' ? 'Previous practice' : '上一章习题'}
          </button>
          <button
            onClick={() => goToChapter(currentNumber + 1)}
            disabled={currentNumber >= chapters.length}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {language === 'en' ? 'Next practice' : '下一章习题'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </main>
      </div>

      {showChat && (
        <div className="w-96 flex-shrink-0 border-l border-apple-border">
          <ChatPanel
            onClose={() => setShowChat(false)}
            externalMessages={chatMessages}
            onMessagesChange={setChatMessages}
            onClearHistory={clearChatHistory}
          />
        </div>
      )}
    </div>
  );
}
